import { sb } from '../supabase';
import { fetchVerificationQueue } from './consoleVerification';
import { stageKeyOf } from './managedStages';
import { fetchRequestFees } from './requestFee';

// ── "Today" — the console's front page, as a work queue ─────────────────────
//
// The console used to open on six counters. A counter tells you a number and
// leaves you to go find the thing; an operations console is used the other way
// round — you arrive knowing there is work and wanting the next item, not the
// total. So this returns ROWS, each one a thing waiting on a person, each with
// the verb that resolves it and where that verb lives.
//
// Four streams, named by the owner on 2026-09-18:
//   1. a supplier registered, or uploaded their documents, and is waiting on us
//   2. a new managed request arrived
//   3. a trader asked a factory for a quote
//   4. a trader posted an open request for offers
//
// Plus the two registration cards that sit above them: who signed up, traders
// on one side and suppliers on the other.
//
// Ordering is by how long the item has been waiting, oldest first. That is the
// only ordering an operations queue can defend: the thing that has waited
// longest is the thing most likely to have been forgotten.

// How many stalled suppliers stand for the rest on the front page. See the note
// where it is applied.
const CHASE_LIMIT = 5;

const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');
const iso = (d) => new Date(d).toISOString();
const daysAgo = (n) => iso(Date.now() - n * 86400000);

// A failed sub-query degrades that stream to empty and SAYS SO in the console.
// A silent catch here would show an empty, calm front page while the work piles
// up behind it — the worst possible failure for a screen whose whole job is to
// tell you what is waiting.
const safe = (label, p) => p.then(
  (r) => {
    if (r?.error) console.error(`[consoleToday] ${label} failed:`, r.error.message || r.error);
    return r;
  },
  (e) => { console.error(`[consoleToday] ${label} threw:`, e?.message || e); return { data: [], error: e }; },
);

const titleOf = (r, isAr) => nf(isAr ? r.title_ar : r.title_en)
  || nf(r.title_ar) || nf(r.title_en) || nf(r.title_zh) || '';

export async function fetchToday({ isAr = true } = {}) {
  const week = daysAgo(7);
  // Local midnight, not "24 hours ago": the owner asked for today's number, and
  // today is a calendar day to the person reading it. A rolling window would say
  // "1 today" at 9am for someone who signed up at 11pm yesterday.
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const day = midnight.toISOString();

  const [queue, reqRes, tradersRes, supsRes, invitesRes] = await Promise.all([
    fetchVerificationQueue().catch((e) => {
      console.error('[consoleToday] verification queue failed:', e?.message || e);
      return { rows: [], buckets: { ready: [], incomplete: [], not_started: [], verified: [], rejected: [] } };
    }),
    safe('requests', sb.from('requests')
      .select('id, request_ref, title_ar, title_en, title_zh, quantity, unit, request_kind, sourcing_mode, status, managed_status, buyer_id, created_at')
      .order('created_at', { ascending: false }).limit(200)),
    safe('traders', sb.from('profiles').select('id, full_name, company_name, city, country, created_at')
      .in('role', ['buyer', 'trader']).order('created_at', { ascending: false }).limit(200)),
    safe('suppliers', sb.from('profiles').select('id, full_name, company_name, created_at')
      .eq('role', 'supplier').order('created_at', { ascending: false }).limit(200)),
    safe('invites', sb.from('request_factory_invites').select('request_id, status')),
  ]);

  const reqs = reqRes?.data || [];
  // The request fee (2026-09-29). An unpaid fee means the request is waiting on
  // the TRADER, not on us — it stays off this queue. A fee flagged refund_due
  // by the daily cron IS waiting on us: someone has to refund it in Telr.
  const feeBy = await fetchRequestFees(sb, reqs.map((r) => r.id));
  const refundDueRes = await safe('refund-due fees', sb.from('managed_request_fees')
    .select('request_id, amount, refund_due_at, refund_due_reason, request:requests(request_ref, title_ar, title_en, title_zh, buyer_id)')
    .eq('status', 'refund_due').order('refund_due_at', { ascending: true }));
  const traders = tradersRes?.data || [];
  const supplierProfiles = supsRes?.data || [];

  // How many factories a request has already been sent to. A request with none
  // is the one that needs distributing; one that has gone out is waiting on the
  // factories, not on us.
  const inviteCount = {};
  for (const i of invitesRes?.data || []) {
    if (i.request_id) inviteCount[i.request_id] = (inviteCount[i.request_id] || 0) + 1;
  }

  const buyerById = {};
  for (const t of traders) buyerById[t.id] = t;
  const buyerName = (id) => {
    const b = buyerById[id];
    return b ? (nf(b.company_name) || nf(b.full_name) || null) : null;
  };

  const items = [];

  // ── 1. suppliers ──────────────────────────────────────────────────────────
  // Documents in and reviewable: the only stream where WE are the bottleneck and
  // a decision is one click away. It outranks everything else for that reason.
  for (const s of queue.buckets.ready || []) {
    items.push({
      id: `sup-ready-${s.id}`,
      stream: 'suppliers',
      severity: 'review',
      title: nf(s.company_name) || nf(s.company_name_latin) || nf(s.full_name) || '—',
      subtitle: nf(s.city) || nf(s.country) || '',
      note: s.docCount ? (isAr ? `${s.docCount} وثيقة مرفوعة` : `${s.docCount} documents uploaded`) : '',
      tagAr: 'ينتظر مراجعتك', tagEn: 'Waiting for review',
      actionAr: 'مراجعة الملف', actionEn: 'Review file',
      href: `/admin2/verification/${s.id}`,
      since: s.created_at,
      sinceLabelAr: 'سجّل', sinceLabelEn: 'Joined',
    });
  }

  // Registered and stalled. Not a review — a chase. The verb differs and so does
  // where it lands: the same file, which is where the outreach buttons live.
  //
  // Capped, and the cap is the point. Production on 2026-09-18 has 24 stalled
  // suppliers against 2 waiting for review and 1 managed request: uncapped, the
  // two things actually waiting on a DECISION are pushed off the first screen by
  // a backlog that is waiting on someone else. Twenty-four people who have not
  // answered is a campaign, not a day's work, so the oldest few stand for it and
  // the rest are one click away in the list built for exactly that.
  const stalled = [...(queue.buckets.incomplete || []), ...(queue.buckets.not_started || [])]
    .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
  const chaseOverflow = Math.max(0, stalled.length - CHASE_LIMIT);

  for (const s of stalled.slice(0, CHASE_LIMIT)) {
    items.push({
      id: `sup-chase-${s.id}`,
      stream: 'suppliers',
      severity: 'chase',
      title: nf(s.company_name) || nf(s.company_name_latin) || nf(s.full_name) || '—',
      subtitle: nf(s.city) || nf(s.country) || '',
      note: s.completeness && typeof s.completeness.percent === 'number'
        ? (isAr ? `الملف مكتمل ${s.completeness.percent}%` : `Profile ${s.completeness.percent}% complete`)
        : (isAr ? 'لم يرفع أي وثيقة' : 'No documents uploaded'),
      tagAr: 'ملف غير مكتمل', tagEn: 'Incomplete profile',
      actionAr: 'طلب الاستكمال', actionEn: 'Ask to complete',
      href: `/admin2/verification/${s.id}`,
      since: s.created_at,
      sinceLabelAr: 'سجّل', sinceLabelEn: 'Joined',
    });
  }

  // ── 2-4. requests ─────────────────────────────────────────────────────────
  for (const r of reqs) {
    const closed = ['closed', 'completed', 'cancelled', 'canceled', 'fulfilled', 'delivered', 'rejected']
      .includes(String(r.status || '').toLowerCase());
    if (closed) continue;

    const kind = String(r.request_kind || '').toLowerCase();
    const mode = String(r.sourcing_mode || '').toLowerCase();
    const managed = kind === 'managed' || mode === 'managed';
    const title = titleOf(r, isAr) || (isAr ? 'طلب بلا عنوان' : 'Untitled request');
    const ref = nf(r.request_ref);
    const who = buyerName(r.buyer_id);
    const qty = [r.quantity, r.unit].filter(Boolean).join(' ');

    if (managed) {
      // Unpaid request fee: the trader has not confirmed yet. Named, positive.
      if (feeBy[r.id]?.status === 'awaiting_payment') continue;
      // Only a managed request still at the 'received' stage is waiting on the
      // admin. Once it moves to sourcing it is our work in progress, not an
      // unopened item, and putting it here would make the queue un-clearable.
      if (stageKeyOf(r.managed_status) !== 'received') continue;
      items.push({
        id: `req-managed-${r.id}`,
        stream: 'requests',
        severity: 'new',
        title,
        subtitle: [ref, who].filter(Boolean).join(' · '),
        note: qty,
        tagAr: 'طلب مُدار جديد', tagEn: 'New managed request',
        actionAr: 'فتح الطلب', actionEn: 'Open request',
        href: `/admin2/managed/${r.id}`,
        since: r.created_at,
        sinceLabelAr: 'وصل', sinceLabelEn: 'Arrived',
      });
      continue;
    }

    // A quote asked of a specific factory, and an open request for offers. Both
    // wait on the same act — getting it in front of factories — so both carry
    // the same verb and land on the distribution board.
    const sent = inviteCount[r.id] || 0;
    if (sent > 0) continue;
    const factoryTargeted = kind === 'factory';
    items.push({
      id: `req-${r.id}`,
      stream: 'requests',
      severity: 'new',
      title,
      subtitle: [ref, who].filter(Boolean).join(' · '),
      note: qty,
      // Same type label as the Quotations board's `open` chip, renamed with
      // it. Two admin screens naming one type differently is the drift these
      // shared maps exist to prevent — and the Today feed links straight to
      // that board, so the words sit one click apart.
      tagAr: factoryTargeted ? 'طلب عرض سعر' : 'طلب عروض مباشر',
      tagEn: factoryTargeted ? 'Quote request' : 'Direct request',
      actionAr: 'توزيع الطلب', actionEn: 'Distribute',
      href: '/admin2/quotations',
      since: r.created_at,
      sinceLabelAr: 'وصل', sinceLabelEn: 'Arrived',
    });
  }

  // Request fees due a refund (the cron only flags; the money moves by hand).
  for (const f of refundDueRes?.data || []) {
    const r = f.request || {};
    items.push({
      id: `fee-refund-${f.request_id}`,
      stream: 'requests',
      severity: 'review',
      title: titleOf(r, isAr) || (isAr ? 'طلب بلا عنوان' : 'Untitled request'),
      subtitle: [nf(r.request_ref), buyerName(r.buyer_id)].filter(Boolean).join(' · '),
      note: isAr ? `${f.amount} ر.س` : `SAR ${f.amount}`,
      tagAr: 'رسوم مستحقة الاسترداد', tagEn: 'Fee refund due',
      actionAr: 'مراجعة الاسترداد', actionEn: 'Review refund',
      href: '/admin2/request-fees',
      since: f.refund_due_at,
      sinceLabelAr: 'استحق', sinceLabelEn: 'Due since',
    });
  }

  // Oldest first — see the note at the top of the file.
  items.sort((a, b) => String(a.since || '').localeCompare(String(b.since || '')));

  const since = (list, at) => list.filter((p) => p.created_at && p.created_at >= at).length;

  return {
    items,
    chaseOverflow,
    counts: {
      all: items.length,
      suppliers: items.filter((i) => i.stream === 'suppliers').length,
      requests: items.filter((i) => i.stream === 'requests').length,
      review: items.filter((i) => i.severity === 'review').length,
      chase: items.filter((i) => i.severity === 'chase').length,
      new: items.filter((i) => i.severity === 'new').length,
    },
    signups: {
      traders: {
        total: traders.length,
        week: since(traders, week),
        today: since(traders, day),
        newest: traders[0] ? {
          name: nf(traders[0].company_name) || nf(traders[0].full_name) || null,
          at: traders[0].created_at,
        } : null,
      },
      suppliers: {
        total: supplierProfiles.length,
        week: since(supplierProfiles, week),
        today: since(supplierProfiles, day),
        newest: supplierProfiles[0] ? {
          name: nf(supplierProfiles[0].company_name) || nf(supplierProfiles[0].full_name) || null,
          at: supplierProfiles[0].created_at,
        } : null,
      },
    },
  };
}
